import React, { useMemo } from 'react';

import { Typography } from '@mui/material';
import type { GridToolbarContainerProps } from '@mui/x-data-grid-premium';
import type { DisplayMode, DocumentStatusFilterType, RepositoryView } from 'src/components';
import {
  DocumentsAlertsButton,
  FiltersToolbarButton,
  RepositoryNewButton,
  RepositoryUploadButton,
  RepositoryViewsDropdown,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { DisplayMenu } from 'src/components/shared/DisplayMenu';
import type { GroupableColumn } from 'src/hooks';
import { useDataGridNonSkeletonRowIds, useDataGridSearch, useGroupableColumns } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

export interface AllDocumentsToolbarProps extends GridToolbarContainerProps {
  activeRepositoryView?: RepositoryView;
  alertsProps?: AlertsPropsSubset;
  displayMode?: DisplayMode;
  documentCount?: number;
  documentStatusFilter?: DocumentStatusFilterType;
  onDisplayModeChange?: (mode: DisplayMode) => void;
  onRepositoryViewChange?: (view: RepositoryView) => void;
  onRowGroupingModelChange?: (model: string[]) => void;
  onUploadPickerOpenChange?: (open: boolean) => void;
  repositoryId?: UUID;
  rowGroupingModel?: string[];
  /**
   * Grouping choices beyond the grid's own columns, such as the org's
   * extraction questions. Offered after the grid's columns; a grid column
   * with the same field wins, as it does in the grouping header.
   */
  virtualGroupableColumns?: readonly GroupableColumn[];
  /** True while `virtualGroupableColumns` is still loading. */
  virtualGroupableColumnsLoading?: boolean;
}

type AlertsPropsSubset = React.ComponentProps<typeof DocumentsAlertsButton>;

const useStyles = makeStyles()(theme => ({
  title: {
    flexShrink: 0,
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
}));

export function RepositoryToolbar({
  activeRepositoryView = 'recently-created',
  alertsProps,
  displayMode = 'flat',
  documentStatusFilter,
  onDisplayModeChange,
  onRepositoryViewChange,
  onRowGroupingModelChange,
  onUploadPickerOpenChange,
  repositoryId,
  rowGroupingModel = [],
  virtualGroupableColumns,
  virtualGroupableColumnsLoading,
}: AllDocumentsToolbarProps): JSX.Element | null {
  const { classes: toolbarClasses } = useListToolbarStyles();
  const { classes } = useStyles();
  const { search, setSearch } = useDataGridSearch();
  const gridGroupableColumns = useGroupableColumns();
  const groupableColumns = useMemo(() => {
    const gridFields = new Set(gridGroupableColumns.map(column => column.field));
    const extraColumns = virtualGroupableColumns?.filter(column => !gridFields.has(column.field));
    return [...gridGroupableColumns, ...(extraColumns ?? [])];
  }, [gridGroupableColumns, virtualGroupableColumns]);
  const handleRepositoryViewChange = (view: RepositoryView) => onRepositoryViewChange?.(view);

  return (
    <div className={toolbarClasses.toolbar}>
      <Typography className={classes.title} variant="h6">
        Documents
      </Typography>
      <FiltersToolbarButton />
      <RepositoryViewsDropdown
        activeView={activeRepositoryView}
        onViewChange={handleRepositoryViewChange}
      />
      <Spacer />
      <SearchInput
        aria-label="Search documents"
        placeholder="Search documents..."
        search={search}
        setSearch={setSearch}
      />
      <DisplayMenu
        displayMode={displayMode}
        groupableColumns={groupableColumns}
        groupableColumnsLoading={virtualGroupableColumnsLoading}
        rowGroupingModel={rowGroupingModel}
        onDisplayModeChange={onDisplayModeChange}
        onRowGroupingModelChange={onRowGroupingModelChange}
      />
      {alertsProps && <DocumentsAlertsButton {...alertsProps} />}
      {repositoryId && documentStatusFilter && (
        <>
          <RepositoryUploadButton
            documentStatusFilter={documentStatusFilter}
            repositoryId={repositoryId}
            onPickerOpenChange={onUploadPickerOpenChange}
          />
          <RepositoryNewButton
            documentStatusFilter={documentStatusFilter}
            repositoryId={repositoryId}
          />
        </>
      )}
    </div>
  );
}

export function DocumentCounter({ documentCount }: { documentCount: number }) {
  const nonSkeletonRowIds = useDataGridNonSkeletonRowIds();

  if (!documentCount) return null;

  const totalLoadedCount = Math.min(nonSkeletonRowIds.length, documentCount);

  return (
    <Typography noWrap color="textSecondary" variant="body2">
      {formatNumber(totalLoadedCount)}/{formatNumber(documentCount)} documents loaded
    </Typography>
  );
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}
